/**
 * <TutorStudentDossierCard> — « Son dossier », replié en bas de l'Aperçu (organism).
 *
 * Tout ce que l'ancienne fiche étalait (`ProfilDetails.js`, `StatisticsList`), à un geste : les
 * coordonnées pour un tuteur relecteur (`review_activities`), puis « Informations » en trois groupes,
 * avec les libellés du legacy, puis ce que le jeu dit de l'élève, sur une ligne en sourdine (livre V16
 * p. 35). Une valeur vide ne prend plus de ligne : les champs vides sont nommés en pied (Wajih, 02/11).
 *
 * Au large, le libellé à gauche et sa valeur à droite ; à l'étroit, l'un sous l'autre : c'est la
 * largeur de la carte qui tranche.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { Accordion } from "../../molecules/Accordion"
import { Card } from "../../molecules/Card"
import { formatAmount, formatCount } from "../../utils/gameAmount"
import { joinFrench } from "../../../ports/primary/ui/formatters/textFormatter"
import {
    type UITutorStudentDetail,
    type UITutorStudentDetailGroup,
    type UITutorStudentGame,
    type UITutorStudentProfile,
} from "../../../ports/primary/ui/types/tutorEleves"

/** Au-delà, le libellé et sa valeur tiennent sur une ligne. */
const SIDE_BY_SIDE_FROM = 560

const GROUPS: { key: UITutorStudentDetailGroup; title: string }[] = [
    { key: "school", title: "Scolarité" },
    { key: "portrait", title: "Portrait" },
    { key: "request", title: "Sa demande" },
]

export type TutorStudentDossierCardProps = {
    profile: UITutorStudentProfile
    /** Pour une story : le dossier ouvert. Défaut `false`. */
    defaultExpanded?: boolean
    testID?: string
}

function isFilled(detail: UITutorStudentDetail): boolean {
    return (
        (detail.value != null && detail.value !== "") ||
        (detail.subjects != null && detail.subjects.length > 0) ||
        (detail.chips != null && detail.chips.length > 0)
    )
}

/** « Exploratrice · 860 XP · Quêtes du champion finies : 3/5 · 42ᵉ au classement · 3 arbres plantés · 1 repas offert ». */
function gameLine(game: UITutorStudentGame): string {
    const parts: string[] = []
    if (game.title) parts.push(game.title)
    if (game.xp != null && game.xp > 0) parts.push(formatAmount(game.xp, "xp"))
    if (game.questsLabel) parts.push(game.questsLabel)
    if (game.rank != null) parts.push(`${formatCount(game.rank)}ᵉ au classement`)
    if (game.trees != null && game.trees > 0) {
        parts.push(`${formatCount(game.trees)} ${game.trees > 1 ? "arbres plantés" : "arbre planté"}`)
    }
    if (game.meals != null && game.meals > 0) {
        parts.push(`${formatCount(game.meals)} ${game.meals > 1 ? "repas offerts" : "repas offert"}`)
    }
    return parts.join(" · ")
}

function DetailValue(props: { detail: UITutorStudentDetail; alignEnd: boolean }) {
    const { detail, alignEnd } = props
    if (detail.subjects != null && detail.subjects.length > 0) {
        return (
            <Row gap="2" style={styles.chips(alignEnd)}>
                {detail.subjects.map((subject) => (
                    <SubjectBadge key={subject.id} subject={subject} showLabel fullLabel />
                ))}
            </Row>
        )
    }
    if (detail.chips != null && detail.chips.length > 0) {
        return (
            <Row gap="2" style={styles.chips(alignEnd)}>
                {detail.chips.map((chip) => (
                    <Badge key={chip} text={chip} variant="neutral" />
                ))}
            </Row>
        )
    }
    return <Text size="text-md">{detail.value}</Text>
}

function DetailRows(props: { details: UITutorStudentDetail[]; sideBySide: boolean }) {
    const { details, sideBySide } = props
    return (
        <Stack gap="2">
            {details.map((detail) =>
                sideBySide ? (
                    <Row key={detail.label} gap="4" align="start">
                        <Text size="text-sm" color="text-muted" style={styles.label}>
                            {detail.label}
                        </Text>
                        <View style={styles.value}>
                            <DetailValue detail={detail} alignEnd />
                        </View>
                    </Row>
                ) : (
                    <Stack key={detail.label} gap="0">
                        <Text size="text-sm" color="text-muted">
                            {detail.label}
                        </Text>
                        <DetailValue detail={detail} alignEnd={false} />
                    </Stack>
                )
            )}
        </Stack>
    )
}

export function TutorStudentDossierCard(props: TutorStudentDossierCardProps) {
    const { profile, defaultExpanded = false, testID } = props
    const { onLayout, atLeast: sideBySide } = useWidthAtLeast(SIDE_BY_SIDE_FROM)
    const filled = profile.details.filter(isFilled)
    const empty = profile.details.filter((detail) => !isFilled(detail))
    const game = gameLine(profile.game)

    return (
        <Card p="0" testID={testID}>
            <Accordion
                defaultExpanded={defaultExpanded}
                header={(expanded) => (
                    <Row gap="3" align="center" style={styles.header}>
                        <Icon name="user-graduate" size={20} color="primary" />
                        <Stack gap="0" style={styles.flex}>
                            <Text size="text-md" weight="bold">
                                Son dossier
                            </Text>
                            {!expanded && (
                                <Text size="text-sm" color="text-muted">
                                    Scolarité, portrait, sa demande, le jeu
                                </Text>
                            )}
                        </Stack>
                        <Icon name={expanded ? "chevron-up" : "chevron-down"} size={20} color="text-muted" />
                    </Row>
                )}
            >
                <View onLayout={onLayout} style={styles.body}>
                    <Stack gap="4">
                        {profile.contacts != null && (
                            <Stack gap="2">
                                <SectionLabel>Coordonnées</SectionLabel>
                                <DetailRows
                                    sideBySide={sideBySide}
                                    details={[
                                        { label: "E-mail", value: profile.contacts.email },
                                        { label: "Numéro de téléphone", value: profile.contacts.phone },
                                    ]}
                                />
                            </Stack>
                        )}
                        {GROUPS.map((group) => {
                            const details = filled.filter((detail) => (detail.group ?? "request") === group.key)
                            return details.length === 0 ? null : (
                                <Stack key={group.key} gap="2">
                                    <SectionLabel>{group.title}</SectionLabel>
                                    <DetailRows details={details} sideBySide={sideBySide} />
                                </Stack>
                            )
                        })}
                        {game !== "" && (
                            <Stack gap="2">
                                <SectionLabel>Le jeu</SectionLabel>
                                <Text size="text-sm">{game}</Text>
                            </Stack>
                        )}
                        {empty.length > 0 && (
                            <Text size="text-sm" color="text-muted">
                                {`Non renseignés : ${joinFrench(empty.map((detail) => detail.label))}.`}
                            </Text>
                        )}
                    </Stack>
                </View>
            </Accordion>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    header: { padding: theme.spacing["4"] },
    flex: { flex: 1, minWidth: 0 },
    body: { paddingHorizontal: theme.spacing["4"], paddingBottom: theme.spacing["4"] },
    label: { flexBasis: "45%", flexShrink: 0 },
    value: { flex: 1, minWidth: 0, alignItems: "flex-end" },
    chips: (alignEnd: boolean) => ({
        flexWrap: "wrap" as const,
        justifyContent: alignEnd ? ("flex-end" as const) : ("flex-start" as const),
    }),
}))
